<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeTabs :value="activeTab">
    <PrimeTabList>
      <PrimeTab :value="0">{{ $t("spaces") }}</PrimeTab>
      <PrimeTab v-if="showUserHome" :value="1">{{ $t("userHomes") }}</PrimeTab>
    </PrimeTabList>
    <PrimeTabPanels>
      <PrimeTabPanel :value="0">
        <PrimeTree
          v-model:selection-keys="selectedKey"
          v-model:expanded-keys="expandedKeys"
          :value="nodes"
          selection-mode="single"
          scroll-height="40rem"
          class="w-full"
          @node-select="onNodeSelect"
          @node-expand="(node) => onNodeExpand(node, nodes)"
        />
      </PrimeTabPanel>
      <PrimeTabPanel v-if="showUserHome" :value="1">
        <PrimeTree
          v-model:selection-keys="selectedKey"
          v-model:expanded-keys="expandedKeys"
          :value="userHomesNodes"
          selection-mode="single"
          class="w-full md:w-30rem"
          scroll-height="40rem"
          @node-select="onNodeSelect"
          @node-expand="(node) => onNodeExpand(node, userHomesNodes)"
        />
      </PrimeTabPanel>
    </PrimeTabPanels>
  </PrimeTabs>
</template>
<script>
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import { useNavigationStore } from "@/stores/navigation.js";
import libVueActionService from "@/services/LibVueActionService.js";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";
import Tree from "primevue/tree";

const SPLITTER = "|";

export default {
  name: "TreeExplorer",
  components: {
    PrimeTabs: Tabs,
    PrimeTabList: TabList,
    PrimeTab: Tab,
    PrimeTabPanels: TabPanels,
    PrimeTabPanel: TabPanel,
    PrimeTree: Tree,
  },
  inject: ["actionsService"],
  props: {
    currentNode: {
      type: Object,
      default: () => ({}),
    },
    showUserHome: {
      type: Boolean,
      default: false,
    },
    showFiles: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["selectNode"],
  data() {
    return {
      activeTab: 0,
      userHomesNodes: [],
      nodes: [],
      selectedKey: null,
      splitedKey: null,
      expandedKeys: {},
    };
  },
  async mounted() {
    try {
      // Initialiser seulement initUserHomeTree si showUserHome est vrai
      const promises = [this.initTree()];
      if (this.showUserHome) {
        promises.push(this.initUserHomeTree());
      }

      const [nodes, userHomesNodes] = await Promise.all(promises);

      this.nodes = nodes;
      if (this.showUserHome) {
        this.userHomesNodes = userHomesNodes;
      }

      if (this.currentNode.path) {
        await this.openCurrentPath();
      } else {
        console.warn(
          "TreeExplorer: can’t select folder, no path on current node",
        );
      }
    } catch (error) {
      console.error("Error initializing trees:", error);
    }
  },
  methods: {
    async initTree() {
      this.splitedKey = null;
      try {
        if (this.actionsService?.getInitialTree) {
          return await this.actionsService.getInitialTree();
        } else {
          return await libVueActionService.getInitialTree();
        }
      } catch (error) {
        console.error("Error initializing tree:", error);
        return [];
      }
    },
    async initUserHomeTree() {
      let opts = {
        include: [
          "isFavorite",
          "properties",
          "allowableOperations",
          "permissions",
          "path",
          "isLocked",
          "aspectNames",
        ],
        skipCount: 0,
        maxItems: 100,
      };
      let res = await alfrescoNodeService.getUserHomeNode();
      let userHomesNodes = await alfrescoNodeService.getChildrenWithOpts(
        res.list.entries[0].entry.id,
        opts,
        false,
      );
      return userHomesNodes.children
        .filter(
          (userHomes) =>
            !userHomes.aspectNames ||
            !userHomes.aspectNames.includes("pm:hidden"),
        )
        .map((userHomes) => {
          return {
            key: userHomes.id,
            label: userHomes.name,
            icon: "ri-inbox-line",
            data: userHomes,
            children: [],
            leaf: false,
          };
        });
    },
    onNodeSelect(node) {
      this.$emit("selectNode", node.data, node.label);
    },
    async onNodeExpand(node, nodes) {
      let opts = {
        include: [
          "isFavorite",
          "properties",
          "allowableOperations",
          "permissions",
          "path",
          "isLocked",
          "aspectNames",
        ],
        skipCount: 0,
        maxItems: 500,
      };
      try {
        const data = await alfrescoNodeService.getChildrenWithOpts(
          this.getNodeId(node.data),
          opts,
          false,
        );
        const children = data.children;
        let treeChildren = children
          .filter((child) => {
            const isHidden =
              child.aspectNames && child.aspectNames.includes("pm:hidden");
            if (isHidden) {
              return false;
            }
            if (this.showFiles) {
              return child.isFolder || child.isFile;
            }
            return child.isFolder;
          })
          .map((child) => ({
            key: `${node.key}${SPLITTER}${child.id}`,
            label: child.name,
            icon: child.isFolder ? "ri-folder-2-line" : "ri-file-line",
            data: child,
            children: [],
            leaf: child.isFile,
          }))
          .sort();
        return this.addChildren(treeChildren, node, nodes);
      } catch (err) {
        console.error("getNodeChildren Error", err);
      }
    },
    addChildren(children, parent, nodes) {
      this.splitedKey = parent.key.split(SPLITTER);
      /* if we expand a root space */
      if (this.splitedKey.length === 1) {
        return nodes
          .filter((child) => child.key === parent.key)
          .map((racine) => (racine.children = children));
      } else {
        return this.addElementChildren(nodes, parent.key, children, 0);
      }
    },
    /**
     * Recursive function to cross over a tree
     * Permit to add children to a found parent
     * @param tree the part of tree we are searching in
     * @param key for example "3-1-1"
     * @param children children to add when parent is find
     * @param step to find parent
     */
    addElementChildren(tree, key, children, step) {
      let parent = tree.find((potentialParent) =>
        this.containsKey(potentialParent.key, key, step),
      );
      if (parent) {
        if (parent.key === key) {
          parent.children = children;
          return null;
        } else if (parent.children) {
          this.addElementChildren(parent.children, key, children, step + 1);
          return null;
        }
      }
    },
    containsKey(shortKey, longKey, step) {
      // is shortkey in longKey ?
      let splitedKey = longKey.split(SPLITTER);
      let parentSplitedKey = shortKey.split(SPLITTER);
      //let's compare arrays for the first "step" steps only
      for (let i = 0; i <= step; i++) {
        if (splitedKey[i] !== parentSplitedKey[i]) {
          return false;
        }
      }
      return true;
    },
    getNodeId(node) {
      if (node === undefined) {
        throw Error("Can't get NodeId from undefined");
      }
      return node.guid ? node.guid : node.id;
    },
    async openCurrentPath() {
      try {
        this.activeTab = useNavigationStore().isInUserHome ? 1 : 0;
        let nodes = useNavigationStore().isInUserHome
          ? this.userHomesNodes
          : this.nodes;
        let keys = null;

        if (this.actionsService?.buildPathKeys) {
          keys = this.actionsService.buildPathKeys(this.currentNode);
        } else {
          keys = libVueActionService.buildPathKeys(this.currentNode);
        }

        await libVueActionService.expandPathKeys({
          keys,
          nodes,
          expandedKeys: this.expandedKeys,
          onNodeExpand: this.onNodeExpand,
          currentNode: this.currentNode,
        });
      } catch (error) {
        console.error("Error opening path:", error);
      }
    },
  },
};
</script>
